/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Search 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/search/Search.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 3.
 */
import * as parent from '@components/form/field/input/Input';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Search.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 실시간 검색을 실행할지 여부
     */
    liveSearch?: boolean;

    /**
     * 검색을 시행할 함수
     */
    handler?: (keyword: string, field: T) => Promise<void>;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Search extends parent.Input {
    /**
     * Input 확장타입
     */
    inputVariant = 'search';

    /**
     * 실시간 검색을 실행할지 여부
     */
    liveSearch: boolean;

    /**
     * 검색을 시행할 함수
     */
    handler: (keyword: string, field: Search) => Promise<void>;

    /**
     * 검색 중인지 여부
     */
    searching: boolean = false;

    /**
     *  마지막 검색어
     */
    lastKeyword: string | null = null;

    /**
     * 실시간 검색 딜레이 타이머
     */
    liveSearchTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * 검색버튼 DOM
     */
    $button: Dom = null;

    /**
     * 검색필드를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Search> = null) {
        super(properties);

        this.liveSearch = this.properties.liveSearch === true;
        this.handler = this.properties.handler ?? null;
        this.clearable = true;
    }

    /**
     * INPUT 필드 DOM 을 가져온다.
     *
     * @returns INPUT 필드 DOM
     */
    $getInput(): Dom<HTMLInputElement> {
        if (this.$input === null) {
            this.$input = super.$getInput(['blur']);

            this.$input.on('input', async (me) => {
                let value = me.getValue();
                if (value.length == 0) {
                    value = null;
                }
                await this.setValue(value);
                if (this.liveSearch == true) {
                    this.#liveSearching(value);
                }
            });

            this.$input.on('keydown', (_, e) => {
                if (e.key == 'Enter') {
                    this.search();
                    e.preventDefault();
                }

                if (e.key != 'Escape') {
                    e.stopImmediatePropagation();
                }
            });
        }

        return this.$input;
    }

    /**
     * 검색버튼 DOM 을 가져온다.
     *
     * @returns 검색버튼
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = Html.create('button', {
                type: 'button',
                'data-action': 'search',
            });

            this.$button.on('click', (_, e: MouseEvent) => {
                this.search();
                e.preventDefault();
                e.stopImmediatePropagation();
            });
        }

        return this.$button;
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<string> {
        return (this.value ?? '').toString();
    }

    /**
     * 검색을 시작한다.
     *
     * @param keyword - 검색할 키워드 (NULL 인 경우 현재 필드값으로 검색한다.)
     */
    async search(keyword: string = null): Promise<void> {
        if (this.searching == true) {
            return;
        }

        this.$getButton().setDisabled(true);
        this.$getButton().addClass('loading');

        this.searching = true;
        keyword ??= await this.getValue();
        if (this.handler !== null) {
            await this.handler(keyword, this);
        }

        this.searching = false;

        if (this.lastKeyword !== null && this.lastKeyword != keyword) {
            await this.search(this.lastKeyword);
        } else {
            this.$getButton().setDisabled(false);
            this.$getButton().removeClass('loading');
            this.lastKeyword = null;
        }
    }

    /**
     * 실시간 검색을 시작한다.
     *
     * @param keyword - 실시간 검색을 하기 위한 키워드
     */
    #liveSearching(keyword: string = null): Promise<void> {
        this.lastKeyword = keyword;

        if (this.searching == true) {
            return;
        }

        if (this.liveSearchTimer !== null) {
            clearTimeout(this.liveSearchTimer);
        }
        this.liveSearchTimer = setTimeout(() => {
            this.liveSearchTimer = null;
            this.search(this.lastKeyword);
        }, 200);
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.$getButton().setDisabled(disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        await super.renderField();
        this.$getContent().append(this.$getButton());
    }
}
